import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import { getRouteStackPath } from '@portal/config/routes';
import { translate } from '@portal/services/i18n';
import React, { useEffect, useMemo, useState } from 'react';

import {
  CloseCircleOutlined,
  CreditCardOutlined,
  MobileOutlined,
} from '@ant-design/icons';
import { CardActions, SettingsActions } from '@portal/actions';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedCheckbox from '@portal/components/AdvancedCheckbox/AdvancedCheckbox';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import CardContact from '@portal/components/CardContact/CardContact';
import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { cards as cardsFlag } from '@portal/config/app.json';
import ErrorsMessages, { PinErrors } from '@portal/enum/errors';
import { useNavigation } from '@portal/hooks/useNavigation';
import useReduxState from '@portal/hooks/useReduxState';
import { CardFunction, CardType } from '@portal/models/enumerators/cards';
import { encrypt } from '@portal/repositories/securitySdkConfig';
import { maskCEP, maskCpfOrCnpj, unmaskField } from '@portal/services/masks';
import { Alert, Col, Modal, Row } from 'antd';
import { DateTime } from 'luxon';
import { useDispatch } from 'react-redux';
import * as MessageService from '~/services/message';
import './RequestCard.less';

type ErrorResolverParams = {
  err: { message: string };
  fallbackKey: string;
};

const RequestCard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const {
    theme: { currentTheme },
    user: { me },
    cards: { cards },
    settings: { remoteConfig },
  } = useReduxState();

  const [cardType, setCardType] = useState<CardType | null>(null);
  const [cardOption, setCardOption] = useState<CardFunction>(
    CardFunction.DEBIT
  );
  const [activateCredit, setActivateCredit] = useState(false);
  const [selectedCard, setSelectedCard] = useState<CardType | null>(null);
  const [pinModal, setPinModal] = useState(false);
  const [openModalTerms, setOpenModalTerms] = useState(false);
  const [cardData, setCardData] = useState({
    cardName: '',
    cardNickName: '',
  });
  const [address, setAddress] = useState<models.Address>({
    zipCode: '',
    street: '',
    addressNumber: '',
    complement: '',
    neighborhood: '',
    city: '',
  });
  const [openCardContactModal, setOpenCardContactModal] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [functionalityUnavailable, setFunctionalityUnavailable] =
    useState(false);

  useEffect(() => {
    dispatch(
      CardActions.getAllCards(me?.id ?? '', cards.length === 0, (err) => {
        if (err) {
          setFunctionalityUnavailable(true);
        }
      })
    );
  }, [dispatch, me]);

  useEffect(() => {
    if (!remoteConfig) {
      dispatch(SettingsActions.getRemoteConfig());
    }
  }, [dispatch, remoteConfig]);

  const getFirstAndLastName = (fullName?: string): string => {
    if (!fullName) return '';

    const parts = fullName.trim().split(/\s+/);

    if (parts.length === 1) return parts[0];

    const first = parts[0];
    const last = parts[parts.length - 1];

    return `${first} ${last}`;
  };

  useEffect(() => {
    if (me) {
      setCardData((prev) => ({
        ...prev,
        cardName: me.account.name ?? '',
        cardNickName: getFirstAndLastName(me.account.name ?? ''),
      }));

      setAddress((prev) => ({
        ...prev,
        street: me.account.address?.street ?? '',
        addressNumber: me.account.address?.addressNumber ?? '',
        complement:
          me.account.address?.complement ?? translate('SHARED.MISSING_DATA'),
        neighborhood: me.account.address?.neighborhood ?? '',
        city: me.account.address?.city ?? '',
        zipCode: me.account.address?.zipCode ?? '',
      }));
    }
  }, [me]);

  const defaultExpiration = useMemo(() => {
    const d = DateTime.now().plus({ years: 5 }).toJSDate();
    d.setUTCHours(0, 0, 0, 0);

    return d.toISOString();
  }, []);

  const renderCardType = () => {
    const cardFunctions = [
      {
        key: CardFunction.DEBIT,
        label: translate(
          'PAGES.CARDS.REQUEST_CARD_V2.CARD_TYPE.CARD_DEBIT.TITLE'
        ),
        description: translate(
          'PAGES.CARDS.REQUEST_CARD_V2.CARD_TYPE.CARD_DEBIT.TEXT'
        ),
        flag: 'DEB',
        visible: cardsFlag.cardDebit,
      },
      {
        key: CardFunction.CREDIT,
        label: translate(
          'PAGES.CARDS.REQUEST_CARD_V2.CARD_TYPE.CARD_CREDIT.TITLE'
        ),
        description: translate(
          'PAGES.CARDS.REQUEST_CARD_V2.CARD_TYPE.CARD_CREDIT.TEXT'
        ),
        flag: 'CRED',
        visible: cardsFlag.cardCredit,
      },
      {
        key: CardFunction.MULTIPLE,
        label: translate(
          'PAGES.CARDS.REQUEST_CARD_V2.CARD_TYPE.CARD_MULTIPLE.TITLE'
        ),
        description: translate(
          'PAGES.CARDS.REQUEST_CARD_V2.CARD_TYPE.CARD_MULTIPLE.TEXT'
        ),
        flag: 'MIX',
        visible: cardsFlag.cardCrebito,
      },
    ];

    const cardFormats = [
      {
        key: CardType.VIRTUAL,
        label: translate(
          'PAGES.CARDS.REQUEST_CARD_V2.BUTTONS.VIRTUAL_CARD.TITLE'
        ),
        description: translate(
          'PAGES.CARDS.REQUEST_CARD_V2.BUTTONS.VIRTUAL_CARD.TEXT'
        ),
        icon: <MobileOutlined />,
        disabled: cards.some((card) => card.type === CardType.VIRTUAL),
      },
      {
        key: CardType.PHYSICAL,
        label: translate(
          'PAGES.CARDS.REQUEST_CARD_V2.BUTTONS.PHYSICAL_CARD.TITLE'
        ),
        description: translate(
          'PAGES.CARDS.REQUEST_CARD_V2.BUTTONS.PHYSICAL_CARD.TEXT'
        ),
        icon: <CreditCardOutlined />,
        disabled: cards.some((card) => card.type === CardType.PHYSICAL),
      },
    ];

    const getOptionStyle = (selected: boolean) => ({
      background: selected ? currentTheme.background : 'white',
      border: `1px solid ${currentTheme.borderColor}`,
    });

    const getIconStyle = (selected: boolean) => ({
      background: selected ? 'white' : currentTheme.background,
      border: `1px solid ${currentTheme.borderColor}`,
    });

    return (
      <div
        className="request-card-v2__card"
        style={{ borderTop: `4px solid ${currentTheme.borderColor}` }}
      >
        <ThemedText variant="h4">
          {translate('PAGES.CARDS.REQUEST_CARD_V2.CARD_TYPE.TYPE')}
        </ThemedText>
        <div className="request-card-v2__card__grid">
          {cardFunctions
            .filter((item) => item.visible)
            .map((item) => {
              const selected = cardOption === item.key;

              return (
                <button
                  key={item.key}
                  onClick={() => {
                    setCardOption(item.key);
                    if (item.key !== CardFunction.CREDIT) {
                      setActivateCredit(false);
                    }
                  }}
                  className={`request-card-v2__card__item__option ${
                    selected ? 'selected' : ''
                  }`}
                  style={getOptionStyle(selected)}
                >
                  <div
                    className="request-card-v2__card__item__option__icon"
                    style={getIconStyle(selected)}
                  >
                    {item.flag}
                  </div>
                  <div className="request-card-v2__card__info">
                    <ThemedText
                      variant="body"
                      className="request-card-v2__card__item__option__text"
                    >
                      {item.label}
                    </ThemedText>
                    <ThemedText variant="body">{item.description}</ThemedText>
                  </div>
                </button>
              );
            })}
        </div>
        {cardsFlag.enableFunctionCardCredit ? (
          <AdvancedCheckbox
            label={translate(
              'PAGES.CARDS.REQUEST_CARD_V2.CARD_TYPE.ACTIVATE_CREDIT'
            )}
            checked={activateCredit}
            onChange={setActivateCredit}
          />
        ) : null}
        <div className="request-card-v2__divider" />
        <ThemedText variant="h4">
          {translate('PAGES.CARDS.REQUEST_CARD_V2.CARD_TYPE.FORMAT')}
        </ThemedText>
        <div className="request-card-v2__format">
          {cardFormats.map((item) => {
            const selected = selectedCard === item.key;

            return (
              <button
                key={item.key}
                onClick={() => {
                  setCardType(item.key);
                  setSelectedCard(item.key);
                }}
                disabled={item.disabled}
                className={`request-card-v2__format__box ${
                  selected ? 'selected' : ''
                }`}
                style={{
                  background: selected
                    ? currentTheme.background
                    : 'transparent',
                  border: `1px solid ${currentTheme.borderColor}`,
                  opacity: item.disabled ? 0.5 : 1,
                }}
              >
                <div
                  className="request-card-v2__card__info__icon"
                  style={{
                    background: selected ? 'white' : currentTheme.background,
                  }}
                >
                  {item.icon}
                </div>
                <div className="request-card-v2__card__info">
                  <ThemedText
                    variant="h4"
                    className="request-card-v2__card__title"
                  >
                    {item.label}
                  </ThemedText>
                  <p>{item.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const validateErrorMessage = (message: string) => {
    return [
      ErrorsMessages.TRANSACTION_NOT_APPROVED,
      ErrorsMessages.TRANSACTION_LAST_ATTEMPT_BEFORE_BLOCK,
      ErrorsMessages.TRANSACTION_FINAL_ATTEMPT,
    ].includes(message as ErrorsMessages);
  };

  const resolveCardErrorMessage = ({
    err,
    fallbackKey,
  }: ErrorResolverParams) => {
    const isKnownError = validateErrorMessage(err.message);

    return {
      message: isKnownError
        ? translate(`PAGES.CARDS.MY_CARDS.DETAILS.${err.message.toUpperCase()}`)
        : fallbackKey,
      isPinError: isKnownError,
    };
  };

  const renderAddressInput = ({
    labelKey,
    value,
    onChange,
    maxLength,
    readOnly = false,
    disabled = true,
    helperText = '',
    securedTextField = false,
  }: {
    labelKey: string;
    value?: string | number | null;
    onChange?: (val: string) => void;
    maxLength?: number;
    readOnly?: boolean;
    disabled?: boolean;
    helperText?: string | boolean;
    securedTextField?: boolean;
  }) => (
    <AdvancedInput
      label={translate(labelKey)}
      variant="outlined"
      value={value ?? ''}
      onChange={onChange}
      maxLength={maxLength}
      readOnly={readOnly}
      disabled={disabled}
      helperText={helperText}
      securedTextField={securedTextField}
    />
  );

  const handleChange = (key: keyof models.Address, value: string) => {
    if (key === 'zipCode') {
      value = unmaskField(value);
    }
    setAddress((prev) => ({ ...prev, [key]: value }));
  };

  const handleChangeCardData = (key: keyof typeof cardData, value: string) => {
    setCardData((prev) => ({ ...prev, [key]: value }));
  };

  const handleDisableButton = (): boolean => {
    const isCardTypeNotSelected = cardType == null;

    const requiredCardFields = [cardData.cardName];

    const missingCardData = !requiredCardFields.every(Boolean);

    if (cardType === CardType.PHYSICAL) {
      const requiredAddressFields = [
        address.zipCode,
        address.street,
        address.addressNumber,
        address.neighborhood,
        address.city,
      ];

      const missingAddressData = requiredAddressFields.some(Boolean) === false;

      return isCardTypeNotSelected || missingCardData || missingAddressData;
    }

    return isCardTypeNotSelected || missingCardData;
  };

  const handleError = (translationKey: string, isPinError = false) => {
    if (isPinError) {
      setPinModal(true);
    }

    MessageService.error(translationKey);
  };

  const handleSubmit = (pinValue: string) => {
    let sequence = 0;
    const alreadySeen: string[] = [];
    const seenTwice: string[] = [];
    for (let index = 0; index < pinValue.length; index++) {
      const digit = pinValue.charAt(index);

      if (seenTwice.includes(digit)) {
        MessageService.error(PinErrors.SAME_DIGIT_MORE_THAN_TWICE, {
          domain: 'validation',
        });
        return;
      }

      if (alreadySeen.includes(digit)) {
        seenTwice.push(digit);
      } else {
        alreadySeen.push(digit);
      }

      if (index < pinValue.length - 1) {
        const nextDigit = parseInt(pinValue.charAt(index + 1), 10);
        const diff = nextDigit - parseInt(digit, 10);
        if (sequence && sequence === diff) {
          MessageService.error(PinErrors.DIGIT_SEQUENCE, {
            domain: 'validation',
          });
          return;
        }
        sequence = Math.abs(diff) === 1 ? diff : 0;
      }
    }

    setPinModal(false);

    if (isSubmitting) return;
    setIsSubmitting(true);

    const encryptedPin = encrypt(pinValue);
    const isVirtual = cardType === CardType.VIRTUAL;

    const callback = (err: Error | null) => {
      if (err) {
        setIsSubmitting(false);
        const { message, isPinError } = resolveCardErrorMessage({
          err,
          fallbackKey: 'PAGES.CARDS.REQUEST_CARD_V2.MESSAGES.ERROR',
        });

        handleError(message, isPinError);
        return;
      }

      MessageService.success('PAGES.CARDS.REQUEST_CARD_V2.MESSAGES.SUCCESS');
      navigate(getRouteStackPath('CARDS', 'MY_CARDS_PAGE'));
    };

    if (isVirtual) {
      dispatch(
        CardActions.createVirtualCardV3(
          defaultExpiration,
          encryptedPin,
          callback
        )
      );
    } else {
      dispatch(CardActions.createPhysicalCardV3(encryptedPin, callback));
    }
  };

  const requestCardData = () => {
    const fieldsAddress = [
      {
        labelKey:
          'PAGES.CARDS.REQUEST_CARD_V2.CARD_TYPE.FORMS.ADDRESS.POSTAL_CODE',
        value: maskCEP(address?.zipCode ?? ''),
        onChange: (val: string) => handleChange('zipCode', val),
        autoFocus: true,
      },
      {
        labelKey: 'PAGES.CARDS.REQUEST_CARD_V2.CARD_TYPE.FORMS.ADDRESS.STREET',
        value: address.street,
        onChange: (val: string) => handleChange('street', val),
      },
      {
        labelKey: 'PAGES.CARDS.REQUEST_CARD_V2.CARD_TYPE.FORMS.ADDRESS.NUMBER',
        value: address.addressNumber,
        onChange: (val: string) => handleChange('addressNumber', val),
      },
      {
        labelKey:
          'PAGES.CARDS.REQUEST_CARD_V2.CARD_TYPE.FORMS.ADDRESS.COMPLEMENT',
        value: address.complement,
        onChange: (val: string) => handleChange('complement', val),
      },
      {
        labelKey:
          'PAGES.CARDS.REQUEST_CARD_V2.CARD_TYPE.FORMS.ADDRESS.NEIGHBORHOOD',
        value: address.neighborhood,
        onChange: (val: string) => handleChange('neighborhood', val),
      },
      {
        labelKey: 'PAGES.CARDS.REQUEST_CARD_V2.CARD_TYPE.FORMS.ADDRESS.CITY',
        value: address.city,
        onChange: (val: string) => handleChange('city', val),
      },
    ];

    const rawDoc = me?.account?.taxIdentifier?.replace(/\D/g, '') ?? '';
    const documentLabel = rawDoc.length <= 11 ? 'CPF' : 'CNPJ';

    const fields = [
      {
        labelKey: 'PAGES.CARDS.REQUEST_CARD_V2.CARD_TYPE.FORMS.NAME_ON_CARD',
        value: cardData.cardName,
        onChange: (val: string) => handleChangeCardData('cardName', val),
        disabled: true,
      },
      {
        labelKey: documentLabel,
        value: maskCpfOrCnpj(me?.account?.taxIdentifier ?? ''),
        readOnly: true,
        disabled: true,
      },
    ];

    const isPhysical = cardType === CardType.PHYSICAL;

    return (
      <AdvancedCard testId="request-card-data-card">
        <div>
          <ThemedText variant="h3" className="typo-mb-lg">
            {translate('PAGES.CARDS.REQUEST_CARD_V2.CARD_TYPE.FORMS.TITLE')}
          </ThemedText>
          <Row gutter={[16, 16]}>
            {fields.map((field) => (
              <Col span={12} key={field.labelKey}>
                {renderAddressInput(field)}
              </Col>
            ))}
          </Row>
          {isPhysical ? (
            <>
              <ThemedText variant="h3" className="typo-mt-md typo-mb-xl">
                {translate(
                  'PAGES.CARDS.REQUEST_CARD_V2.CARD_TYPE.FORMS.ADDRESS.TITLE'
                )}
              </ThemedText>
              <Row gutter={[16, 16]}>
                {fieldsAddress.map((field) => (
                  <Col span={12} key={field.labelKey}>
                    {renderAddressInput(field)}
                  </Col>
                ))}
              </Row>
            </>
          ) : null}
        </div>
        <div className="request-card-v2__card__buttons">
          <AdvancedButton
            text={translate('PAGES.CARDS.REQUEST_CARD_V2.BUTTONS.REQUEST_CARD')}
            variant="contained"
            disabled={handleDisableButton() || isSubmitting}
            onClick={() => setOpenModalTerms(true)}
          />
        </div>
      </AdvancedCard>
    );
  };

  const handleConfirmTerm = () => {
    setOpenModalTerms(false);
    dispatch(
      CardActions.signatureHash(remoteConfig?.termsOfUseCard ?? '', (err) => {
        setIsSubmitting(false);

        if (err) {
          setOpenModalTerms(true);
          MessageService.error('PAGES.CARDS.REQUEST_CARD_V2.MESSAGES.ERROR');

          return;
        }

        setPinModal(true);
      })
    );
  };

  const handleCloseTerms = () => {
    setOpenModalTerms(false);
    setAcceptTerms(false);
  };

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: false,
            title: translate('COMPONENTS.BREADCRUMB.CARDS'),
            url: getRouteStackPath('CARDS', 'CARDS_PAGE'),
          },
          {
            active: false,
            title: translate('COMPONENTS.BREADCRUMB.NEW_CARD'),
          },
        ]}
      />
      <div className="request-card-v2">
        <AdvancedPanelContentHeader
          testId="request-card-v2-header"
          title={translate('PAGES.CARDS.REQUEST_CARD_V2.TITLE')}
          description={translate('PAGES.CARDS.REQUEST_CARD_V2.TEXT')}
          goBackAction={() => navigate.back()}
        />
        {functionalityUnavailable ? (
          <AdvancedCard testId="request-card-functionality-unavailable">
            <div className="card-details__loading-error">
              <CloseCircleOutlined className="card-details__loading-error__icon" />
              <ThemedText variant="body">
                {translate(
                  'PAGES.CARDS.MY_CARDS.FUNCTIONALITY_UNAVAILABLE.TITLE'
                )}
              </ThemedText>
              <ThemedText variant="body">
                {translate(
                  'PAGES.CARDS.MY_CARDS.FUNCTIONALITY_UNAVAILABLE.DESCRIPTION'
                )}
              </ThemedText>
            </div>
          </AdvancedCard>
        ) : (
          <>
            {selectedCard !== null && selectedCard !== undefined && (
              <div className="request-card-v2__alert">
                <Alert
                  showIcon
                  message={translate(
                    `PAGES.CARDS.REQUEST_CARD_V2.HELPER.${
                      selectedCard === CardType.PHYSICAL
                        ? 'SECOND_TEXT'
                        : 'TEXT'
                    }`
                  )}
                  action={
                    <AdvancedButton
                      text={translate(
                        'PAGES.CARDS.REQUEST_CARD_V2.HELPER.BUTTON'
                      )}
                      onClick={() => setOpenCardContactModal(true)}
                    />
                  }
                />
              </div>
            )}
            <div className="request-card-v2__grid">
              {renderCardType()}
              {requestCardData()}
            </div>
          </>
        )}
      </div>
      <ConfirmationModal
        visible={pinModal}
        setVisible={setPinModal}
        handleConfirmation={handleSubmit}
      />
      <Modal
        open={openModalTerms}
        footer={
          <div className="request-card-v2__buttons">
            <AdvancedButton
              text={translate('PAGES.CARDS.REQUEST_CARD_V2.BUTTONS.CANCEL')}
              variant="outlined"
              color="error"
              onClick={handleCloseTerms}
            />
            <AdvancedButton
              text={translate('PAGES.CARDS.REQUEST_CARD_V2.BUTTONS.CONFIRM')}
              onClick={() => handleConfirmTerm()}
              disabled={!acceptTerms}
            />
          </div>
        }
        title={null}
        closable
        onCancel={handleCloseTerms}
        width={1000}
      >
        <ThemedText variant="body" className="terms-of-use__title">
          {translate(`PAGES.MY_ACCOUNT.TABS.${'TERMS_OF_USE'}`)}
        </ThemedText>
        <iframe
          src={remoteConfig?.termsOfUseCard}
          width="100%"
          height={500}
          sandbox="allow-same-origin"
          referrerPolicy="no-referrer"
        />
        <AdvancedCheckbox
          label={translate('PAGES.CARDS.REQUEST_CARD_V2.TERMS.ACCEPT_TERMS')}
          value={acceptTerms}
          onChange={() => setAcceptTerms((prev) => !prev)}
        />
      </Modal>
      <CardContact
        visible={openCardContactModal}
        onClose={() => setOpenCardContactModal(false)}
      />
    </>
  );
};

export default RequestCard;
